import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import type {
  SupportTicket,
  SupportMessage,
  SupportAttachment,
  SupportMetrics,
  SupportTicketStatus,
  SupportTicketPriority,
  SupportTicketCategory,
} from '../types/database';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ========= Input types =========

export interface CreateTicketInput {
  subject: string;
  category: SupportTicketCategory;
  message: string;
  user_plan?: string;
  user_email?: string;
  user_name?: string;
  priority?: SupportTicketPriority;
}

export interface AdminTicketFilters {
  status?: string | null;
  category?: string | null;
  priority?: string | null;
  search?: string | null;
  page?: number;
  limit?: number;
}

export interface TicketWithMessages extends SupportTicket {
  messages: SupportMessage[];
}

export const supportService = {
  // ========= User-facing =========

  async createTicket(input: CreateTicketInput): Promise<SupportTicket> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(getCurrentCopy().common.serviceErrors.notAuthenticated);

    // Create ticket
    const { data: ticket, error: ticketError } = await supabase
      .from('support_tickets')
      .insert({
        user_id: user.id,
        subject: input.subject,
        category: input.category,
        priority: input.priority || 'medium',
        user_plan: input.user_plan || null,
        user_email: input.user_email || user.email,
        user_name: input.user_name || null,
      })
      .select()
      .single();

    if (ticketError) throw ticketError;

    // Create first message
    const { error: msgError } = await supabase
      .from('support_messages')
      .insert({
        ticket_id: ticket.id,
        sender_id: user.id,
        content: input.message,
        is_admin_reply: false,
        is_internal_note: false,
      });

    if (msgError) throw msgError;

    return ticket as SupportTicket;
  },

  async getMyTickets(statusFilter?: string): Promise<SupportTicket[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(getCurrentCopy().common.serviceErrors.notAuthenticated);

    let query = supabase
      .from('support_tickets')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'open') {
        query = query.in('status', ['open', 'in_progress', 'waiting_user']);
      } else if (statusFilter === 'resolved') {
        query = query.in('status', ['resolved', 'closed']);
      } else {
        query = query.eq('status', statusFilter);
      }
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as SupportTicket[];
  },

  async getTicketWithMessages(ticketId: string): Promise<TicketWithMessages> {
    // Fetch ticket
    const { data: ticket, error: ticketError } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('id', ticketId)
      .single();

    if (ticketError) throw ticketError;

    // Fetch messages (sem JOIN — FK aponta para auth.users, nao profiles)
    const { data: messages, error: msgError } = await supabase
      .from('support_messages')
      .select('*, attachments:support_attachments(*)')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (msgError) throw msgError;

    return {
      ...(ticket as SupportTicket),
      messages: (messages || []) as SupportMessage[],
    };
  },

  async addMessage(ticketId: string, content: string, files?: File[]): Promise<SupportMessage> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(getCurrentCopy().common.serviceErrors.notAuthenticated);

    const { data: msg, error } = await supabase
      .from('support_messages')
      .insert({
        ticket_id: ticketId,
        sender_id: user.id,
        content,
        is_admin_reply: false,
        is_internal_note: false,
      })
      .select()
      .single();

    if (error) throw error;

    // Upload attachments if any
    if (files && files.length > 0) {
      await Promise.all(files.map(file => this.uploadAndLinkAttachment(msg.id, file)));
    }

    // Update ticket last_activity_at
    const { error: updateError } = await supabase
      .from('support_tickets')
      .update({ last_activity_at: new Date().toISOString(), status: 'open' })
      .eq('id', ticketId);

    if (updateError) console.error('Error updating ticket activity:', updateError);

    return msg as SupportMessage;
  },

  async rateTicket(ticketId: string, rating: number, comment?: string): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({
        satisfaction_rating: rating,
        satisfaction_comment: comment || null,
      })
      .eq('id', ticketId);

    if (error) throw error;
  },

  async getMyOpenTicketCount(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('support_tickets')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .in('status', ['open', 'in_progress', 'waiting_user']);

    if (error) throw error;
    return count || 0;
  },

  // ========= Admin-facing =========

  async getMetrics(): Promise<SupportMetrics> {
    const { data, error } = await supabase.rpc('get_support_metrics');
    if (error) throw error;
    return data as SupportMetrics;
  },

  async getAllTickets(filters: AdminTicketFilters = {}): Promise<{ tickets: SupportTicket[]; total: number; totalPages: number }> {
    const { data, error } = await supabase.rpc('get_admin_support_tickets', {
      p_status: filters.status || null,
      p_category: filters.category || null,
      p_priority: filters.priority || null,
      p_search: filters.search || null,
      p_page: filters.page || 1,
      p_limit: filters.limit || 20,
    });

    if (error) throw error;
    return data as { tickets: SupportTicket[]; total: number; totalPages: number };
  },

  async getTicketDetail(ticketId: string): Promise<TicketWithMessages> {
    // Fetch ticket
    const { data: ticket, error: ticketError } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('id', ticketId)
      .single();

    if (ticketError) throw ticketError;

    // Fetch ALL messages (including internal notes for admin)
    const { data: messages, error: msgError } = await supabase
      .from('support_messages')
      .select('*, attachments:support_attachments(*)')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (msgError) throw msgError;

    return {
      ...(ticket as SupportTicket),
      messages: (messages || []) as SupportMessage[],
    };
  },

  async adminReply(ticketId: string, content: string, isInternalNote = false): Promise<SupportMessage> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(getCurrentCopy().common.serviceErrors.notAuthenticated);

    const { data: msg, error } = await supabase
      .from('support_messages')
      .insert({
        ticket_id: ticketId,
        sender_id: user.id,
        content,
        is_admin_reply: true,
        is_internal_note: isInternalNote,
      })
      .select()
      .single();

    if (error) throw error;

    // Update ticket: first_response_at + last_activity_at + status
    const updates: Record<string, unknown> = {
      last_activity_at: new Date().toISOString(),
    };

    if (!isInternalNote) {
      // Set status to waiting_user when admin replies publicly
      updates.status = 'waiting_user';

      // Set first_response_at if this is the first admin reply
      const { data: ticket } = await supabase
        .from('support_tickets')
        .select('first_response_at, user_id')
        .eq('id', ticketId)
        .single();

      if (ticket && !ticket.first_response_at) {
        updates.first_response_at = new Date().toISOString();
      }

      // Create notification for the user
      if (ticket) {
        // Falha de notificacao nao pode quebrar a resposta ao ticket, mas
        // tambem nao pode sumir: o try/catch anterior era inutil (supabase-js
        // devolve { error } em vez de lancar) e escondeu por quatro meses o
        // fato de que 'support_reply' era rejeitado pelo CHECK constraint.
        const { error: notifyError } = await supabase.from('notifications').insert({
          user_id: ticket.user_id,
          actor_id: user.id,
          type: 'support_reply',
          data: {
            ticket_id: ticketId,
            comment_preview: content.substring(0, 100),
          },
        });
        if (notifyError) {
          console.error('support_reply notification failed:', notifyError);
        }
      }
    }

    await supabase
      .from('support_tickets')
      .update(updates)
      .eq('id', ticketId);

    return msg as SupportMessage;
  },

  async updateTicketStatus(ticketId: string, status: SupportTicketStatus): Promise<void> {
    const updates: Record<string, unknown> = { status };

    if (status === 'resolved') {
      updates.resolved_at = new Date().toISOString();
    } else if (status === 'closed') {
      updates.closed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('support_tickets')
      .update(updates)
      .eq('id', ticketId);

    if (error) throw error;
  },

  async updateTicketPriority(ticketId: string, priority: SupportTicketPriority): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({ priority })
      .eq('id', ticketId);

    if (error) throw error;
  },

  async assignTicket(ticketId: string, adminId: string | null): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({ assigned_to: adminId })
      .eq('id', ticketId);

    if (error) throw error;
  },

  async bulkClose(ticketIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({ status: 'closed', closed_at: new Date().toISOString() })
      .in('id', ticketIds);

    if (error) throw error;
  },

  async bulkAssign(ticketIds: string[], adminId: string): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({ assigned_to: adminId })
      .in('id', ticketIds);

    if (error) throw error;
  },

  async getAdminUsers(): Promise<{ id: string; full_name: string | null; email: string }[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('is_admin', true);

    if (error) throw error;
    return data || [];
  },

  // ========= Real-time =========

  subscribeToTicketMessages(ticketId: string, callback: (msg: SupportMessage) => void): RealtimeChannel {
    return supabase
      .channel('support_messages:' + ticketId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => callback(payload.new as SupportMessage)
      )
      .subscribe();
  },

  subscribeToNewTickets(callback: (ticket: SupportTicket) => void): RealtimeChannel {
    return supabase
      .channel('support_tickets:new')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_tickets',
        },
        (payload) => callback(payload.new as SupportTicket)
      )
      .subscribe();
  },

  // ========= Attachments =========

  async uploadAttachment(file: File): Promise<string> {
    const ext = file.name.split('.').pop();
    const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage
      .from('support-attachments')
      .upload(path, file, { contentType: file.type });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('support-attachments')
      .getPublicUrl(path);

    return publicUrl;
  },

  async uploadAndLinkAttachment(messageId: string, file: File): Promise<SupportAttachment> {
    const fileUrl = await this.uploadAttachment(file);

    const { data, error } = await supabase
      .from('support_attachments')
      .insert({
        message_id: messageId,
        file_name: file.name,
        file_url: fileUrl,
        file_size: file.size,
        file_type: file.type,
      })
      .select()
      .single();

    if (error) throw error;
    return data as SupportAttachment;
  },
};
